import { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, Tooltip,
  Alert, CircularProgress, TextField, MenuItem, Select, InputAdornment,
  LinearProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  Tabs, Tab, Avatar,
} from '@mui/material';
import {
  Refresh, Payment, Receipt, Search, FileDownload,
  Email, Visibility, TrendingUp, Warning, CheckCircle,
  AccountBalance, AccessTime, ErrorOutline,
} from '@mui/icons-material';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';
const cfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const fmt = (v) => `$${parseFloat(v || 0).toLocaleString('es-EC', { minimumFractionDigits: 2 })}`;
const pct = (v) => `${parseFloat(v || 0).toFixed(1)}%`;

// ── Colores semáforo antigüedad ──────────────────────────────────────────────
const EDAD_COLOR = (dias) => {
  if (dias <= 30) return { bg: '#e8f5e9', text: '#2e7d32', border: '#4caf50' };
  if (dias <= 60) return { bg: '#fff8e1', text: '#f57f17', border: '#ffc107' };
  if (dias <= 90) return { bg: '#fff3e0', text: '#e65100', border: '#ff9800' };
  return { bg: '#fce4ec', text: '#b71c1c', border: '#f44336' };
};

const PIE_COLORS = ['#4caf50', '#ffc107', '#ff9800', '#f44336'];

// ── Semáforo global ──────────────────────────────────────────────────────────
function SemaforoGlobal({ estado }) {
  const map = {
    SALUDABLE: { color: '#2e7d32', bg: '#e8f5e9', icon: <CheckCircle />, label: 'Cartera saludable' },
    EN_RIESGO: { color: '#f57f17', bg: '#fff8e1', icon: <Warning />,      label: 'Cartera en riesgo' },
    CRITICO:   { color: '#b71c1c', bg: '#fce4ec', icon: <ErrorOutline />, label: 'Cartera crítica' },
  };
  const s = map[estado] || map.SALUDABLE;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderRadius: 2, bgcolor: s.bg, border: `1px solid ${s.color}` }}>
      <Box sx={{ color: s.color, display: 'flex' }}>{s.icon}</Box>
      <Typography sx={{ color: s.color, fontWeight: 700, fontSize: 13 }}>{s.label}</Typography>
    </Box>
  );
}

// ── KPI Card ─────────────────────────────────────────────────────────────────
function Kpi({ icon, label, value, sub, color = '#1565c0', bg = '#e3f2fd' }) {
  return (
    <Paper sx={{ p: 2, borderRadius: 2, borderTop: `3px solid ${color}`, height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .5, color: 'text.secondary' }}>{label}</Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color, mt: 0.25 }}>{value}</Typography>
          {sub && <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{sub}</Typography>}
        </Box>
        <Box sx={{ width: 40, height: 40, borderRadius: 1.5, bgcolor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color }}>{icon}</Box>
      </Box>
    </Paper>
  );
}

export default function CuentasPorCobrar() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingAnalisis, setLoadingAnalisis] = useState(false);
  const [error, setError] = useState(null);
  const [resumen, setResumen] = useState(null);
  const [facturas, setFacturas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [analisis, setAnalisis] = useState(null);
  const [cobros, setCobros] = useState([]);

  // Filtros
  const [busqueda, setBusqueda] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [filtroEdad, setFiltroEdad] = useState('todos');

  // Dialog recordatorio
  const [dlgEmail, setDlgEmail] = useState({ open: false, cliente: null, factura: null });
  const [enviandoEmail, setEnviandoEmail] = useState(false);
  const [mensajeEmail, setMensajeEmail] = useState('');

  useEffect(() => { cargarDatos(); }, []);
  useEffect(() => { if (tab === 1 && !analisis) cargarAnalisis(); }, [tab]);

  const cargarDatos = async () => {
    setLoading(true); setError(null);
    try {
      const [resRes, cliRes, cobRes, facRes, recRes] = await Promise.all([
        axios.get(`${API}/api/cuentas-cobrar/resumen-cartera`, cfg()).catch(() => ({ data: { data: null } })),
        axios.get(`${API}/api/cuentas-cobrar/clientes-con-saldo`, cfg()).catch(() => ({ data: { data: [] } })),
        axios.get(`${API}/api/cuentas-cobrar/cobros`, cfg()).catch(() => ({ data: { data: [] } })),
        axios.get(`${API}/api/facturas`, cfg()).catch(() => ({ data: [] })),
        axios.get(`${API}/api/recibos`, cfg()).catch(() => ({ data: { data: [] } })),
      ]);

      setResumen(resRes.data?.data || resRes.data);
      setClientes(cliRes.data?.data || []);

      const allCobros = cobRes.data?.data || cobRes.data || [];
      setCobros(allCobros);

      const todas = facRes.data || [];
      const recibos = recRes.data?.data || [];
      const hoy = new Date();

      const parseFecha = (s) => {
        if (!s) return null;
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) { const [d,m,y]=s.split('/'); return new Date(+y,+m-1,+d); }
        const d = new Date(s); return isNaN(d) ? null : d;
      };

      const pendientes = todas
        .filter(f => f.estadoSRI === 'AUTORIZADO')
        .map(f => {
          const total = parseFloat(f.totales?.valorPagar || f.totales?.importeTotal || 0);
          const cobrosF = allCobros.filter(c => c.factura_id === (f.id||f.claveAcceso) || c.factura_clave === f.claveAcceso);
          const cobrado = cobrosF.reduce((s,c) => s + parseFloat(c.monto||0) + parseFloat(c.ret_fuente_monto||0) + parseFloat(c.ret_iva_monto||0), 0);
          const saldo = Math.max(0, total - cobrado);
          const fecha = parseFecha(f.fechaEmision) || parseFecha(f.created_at);
          const dias = fecha ? Math.floor((hoy - fecha) / 86400000) : 0;
          return { ...f, total, cobrado, saldo, fecha, dias, tipo: 'FACTURA' };
        })
        .filter(f => f.saldo > 0.01);

      const CREDITO = ['credito_plazo','credito','Crédito','CREDITO'];
      const recPendientes = recibos
        .filter(r => r.estado !== 'ANULADO' && CREDITO.includes(r.forma_pago))
        .map(r => {
          const total = parseFloat(r.totales?.total || 0);
          const cobrosR = allCobros.filter(c => c.factura_id === r.id);
          const cobrado = cobrosR.reduce((s,c) => s + parseFloat(c.monto||0), 0);
          const saldo = Math.max(0, total - cobrado);
          const fecha = parseFecha(r.fecha);
          const dias = fecha ? Math.floor((hoy - fecha) / 86400000) : 0;
          return { ...r, id: r.id, tipo: 'RECIBO', numero_recibo: r.numero, fechaEmision: r.fecha ? new Date(r.fecha).toLocaleDateString('es-EC') : '—', total, cobrado, saldo, fecha, dias };
        })
        .filter(r => r.saldo > 0.01);

      setFacturas([...pendientes, ...recPendientes].sort((a,b) => b.dias - a.dias));
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const cargarAnalisis = async () => {
    setLoadingAnalisis(true);
    try {
      const r = await axios.get(`${API}/api/cuentas-cobrar/analisis`, cfg());
      if (r.data.success) setAnalisis(r.data.data);
    } catch (_) {}
    finally { setLoadingAnalisis(false); }
  };

  // Filtrado
  const facsFiltradas = useMemo(() => {
    return facturas.filter(f => {
      const q = busqueda.toLowerCase();
      const matchQ = !q || (f.cliente?.nombre || f.cliente?.razonSocial || '').toLowerCase().includes(q) || (f.secuencial || '').includes(q) || (f.cliente?.ruc || '').includes(q);
      const matchE = filtroEstado === 'todos' || (filtroEstado === 'parcial' ? f.cobrado > 0 : filtroEstado === 'pendiente' ? f.cobrado === 0 : true);
      const matchA = filtroEdad === 'todos' || (filtroEdad === '0-30' ? f.dias <= 30 : filtroEdad === '31-60' ? f.dias > 30 && f.dias <= 60 : filtroEdad === '61-90' ? f.dias > 60 && f.dias <= 90 : f.dias > 90);
      return matchQ && matchE && matchA;
    });
  }, [facturas, busqueda, filtroEstado, filtroEdad]);

  const totalFiltrado = facsFiltradas.reduce((s,f) => s + f.saldo, 0);

  // Exportar CSV
  const exportarCSV = () => {
    const rows = [['Fecha','Cliente','RUC','Documento','Total','Cobrado','Saldo','Días','Estado']];
    facsFiltradas.forEach(f => {
      const estado = f.cobrado > 0 ? 'Parcial' : 'Pendiente';
      rows.push([f.fechaEmision || '—', f.cliente?.nombre || '—', f.cliente?.ruc || '—', f.secuencial || f.numero_recibo || '—', f.total.toFixed(2), f.cobrado.toFixed(2), f.saldo.toFixed(2), f.dias, estado]);
    });
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `cuentas_cobrar_${new Date().toISOString().split('T')[0]}.csv`; a.click();
  };

  // Enviar recordatorio
  const enviarRecordatorio = async () => {
    if (!dlgEmail.cliente || !dlgEmail.factura) return;
    setEnviandoEmail(true);
    try {
      await axios.post(`${API}/api/email/enviar-prueba`, {
        destinatario: dlgEmail.cliente?.email || '',
        asunto: `Recordatorio de cobro - Factura ${dlgEmail.factura.secuencial || dlgEmail.factura.numero_recibo}`,
        mensaje: mensajeEmail || `Estimado/a ${dlgEmail.cliente?.nombre || 'cliente'}, le recordamos que tiene una factura pendiente de pago por ${fmt(dlgEmail.factura.saldo)}. Por favor proceda al pago a la brevedad posible.`,
      }, cfg());
      setDlgEmail({ open: false, cliente: null, factura: null });
      setMensajeEmail('');
    } catch (_) {}
    finally { setEnviandoEmail(false); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>

      {/* ── HEADER ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Cuentas por Cobrar</Typography>
          <Typography variant="body2" color="text.secondary">Gestión de cartera y seguimiento de cobros</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={cargarDatos} size="small">Actualizar</Button>
          <Button variant="outlined" startIcon={<FileDownload />} onClick={exportarCSV} size="small">Exportar CSV</Button>
          <Button variant="contained" startIcon={<Payment />} onClick={() => navigate('/cuentas-cobrar/registrar-cobro')}
            sx={{ bgcolor: '#1565c0' }}>Registrar Cobro</Button>
          <Button variant="outlined" onClick={() => navigate('/cuentas-cobrar/cobros')}>Ver Cobros</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── KPIs ── */}
      {resumen && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 12, marginBottom: 20 }}>
          <Kpi icon={<AccountBalance />} label="Total Facturado"  value={fmt(resumen.total_facturado)}  sub={`${resumen.cantidad_facturas||0} facturas`}  color="#1565c0" bg="#e3f2fd" />
          <Kpi icon={<CheckCircle />}    label="Total Cobrado"    value={fmt(resumen.total_cobrado)}     sub={`${resumen.facturas_pagadas||0} pagadas`}     color="#2e7d32" bg="#e8f5e9" />
          <Kpi icon={<TrendingUp />}     label="Saldo Pendiente"  value={fmt(resumen.saldo_pendiente)}   sub={`${resumen.facturas_pendientes||0} pendientes`} color="#f57f17" bg="#fff8e1" />
          <Kpi icon={<Warning />}        label="Vencido +90 días" value={fmt(resumen.antiguedad_saldos?.['90+'])} sub="Alto riesgo"  color="#b71c1c" bg="#fce4ec" />
          <Kpi icon={<AccessTime />}     label="% Cobranza"
            value={resumen.total_facturado > 0 ? pct((resumen.total_cobrado / resumen.total_facturado) * 100) : '0%'}
            sub="Tasa de cobro" color="#6a1b9a" bg="#f3e5f5" />
        </div>
      )}

      {/* ── PESTAÑAS ── */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: '1px solid #e0e0e0' }}>
        <Tab label="📋 Cartera" />
        <Tab label="📊 Análisis" />
        <Tab label="👥 Por Cliente" />
      </Tabs>

      {/* ══════════════ TAB 0: CARTERA ══════════════ */}
      {tab === 0 && (
        <>
          {/* Filtros */}
          <Paper sx={{ p: 1.5, mb: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField size="small" placeholder="Buscar cliente, RUC, documento..." value={busqueda}
              onChange={e => setBusqueda(e.target.value)} sx={{ minWidth: 280 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
            <Select size="small" value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)} sx={{ minWidth: 130 }}>
              <MenuItem value="todos">Todos los estados</MenuItem>
              <MenuItem value="pendiente">Sin cobro</MenuItem>
              <MenuItem value="parcial">Cobro parcial</MenuItem>
            </Select>
            <Select size="small" value={filtroEdad} onChange={e => setFiltroEdad(e.target.value)} sx={{ minWidth: 140 }}>
              <MenuItem value="todos">Toda antigüedad</MenuItem>
              <MenuItem value="0-30">0-30 días</MenuItem>
              <MenuItem value="31-60">31-60 días</MenuItem>
              <MenuItem value="61-90">61-90 días</MenuItem>
              <MenuItem value="+90">+90 días</MenuItem>
            </Select>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
              {facsFiltradas.length} doc. · Pendiente: <strong style={{ color: '#d32f2f' }}>{fmt(totalFiltrado)}</strong>
            </Typography>
          </Paper>

          {/* Tabla */}
          <Paper sx={{ borderRadius: 2 }}>
            <Box sx={{ px: 2, py: 1.5, bgcolor: '#1565c0', color: '#fff', borderRadius: '8px 8px 0 0', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Receipt sx={{ fontSize: 20 }} />
              <Typography fontWeight={700}>Documentos Pendientes de Cobro</Typography>
              <Chip label={facsFiltradas.length} size="small" sx={{ bgcolor: '#fff', color: '#1565c0', fontWeight: 700, ml: 1 }} />
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f7fa', '& th': { fontWeight: 700, fontSize: 11, py: 1 } }}>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>RUC / Cédula</TableCell>
                    <TableCell>No. Documento</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Cobrado</TableCell>
                    <TableCell align="right">Saldo</TableCell>
                    <TableCell sx={{ minWidth: 110 }}>Progreso</TableCell>
                    <TableCell align="center">Antigüedad</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {facsFiltradas.length === 0 ? (
                    <TableRow><TableCell colSpan={11} align="center" sx={{ py: 5 }}>
                      <Typography color="text.secondary">No hay documentos con los filtros seleccionados</Typography>
                    </TableCell></TableRow>
                  ) : (
                    facsFiltradas.map((f, i) => {
                      const ec = EDAD_COLOR(f.dias);
                      const progreso = f.total > 0 ? Math.round((f.cobrado / f.total) * 100) : 0;
                      const clienteRuc = f.cliente?.ruc || f.cliente?.cedula || '';
                      return (
                        <TableRow key={f.id || f.claveAcceso} hover
                          sx={{ bgcolor: i % 2 === 0 ? '#fff' : '#fafafa', '& td': { fontSize: 12, py: 0.8 } }}>
                          <TableCell>{f.fechaEmision || '—'}</TableCell>
                          <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {f.cliente?.nombre || f.cliente?.razonSocial || '—'}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{clienteRuc}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                            {f.tipo === 'RECIBO'
                              ? <Chip label={`RCV ${f.numero_recibo}`} size="small" color="secondary" variant="outlined" sx={{ fontSize: 10, height: 20 }} />
                              : String(f.secuencial || '').padStart(9, '0')}
                          </TableCell>
                          <TableCell align="right">{fmt(f.total)}</TableCell>
                          <TableCell align="right" sx={{ color: '#2e7d32' }}>{fmt(f.cobrado)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, color: '#d32f2f' }}>{fmt(f.saldo)}</TableCell>
                          <TableCell sx={{ minWidth: 110 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <LinearProgress variant="determinate" value={progreso}
                                sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: '#e0e0e0', '& .MuiLinearProgress-bar': { bgcolor: progreso === 100 ? '#4caf50' : progreso > 0 ? '#ffc107' : '#f44336' } }} />
                              <Typography sx={{ fontSize: 10, minWidth: 28, color: 'text.secondary' }}>{progreso}%</Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', px: 1, py: 0.25, borderRadius: 1, bgcolor: ec.bg, border: `1px solid ${ec.border}` }}>
                              <Typography sx={{ fontSize: 11, fontWeight: 700, color: ec.text }}>{f.dias}d</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {f.cobrado > 0
                              ? <Chip label="Parcial" color="warning" size="small" sx={{ fontSize: 10, height: 20 }} />
                              : <Chip label="Pendiente" color="info"    size="small" sx={{ fontSize: 10, height: 20 }} />}
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Registrar Cobro">
                              <IconButton size="small" color="success"
                                onClick={() => navigate(`/cuentas-cobrar/registrar-cobro?clienteId=${clienteRuc || f.cliente?.id}`)}>
                                <Payment fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Enviar Recordatorio">
                              <IconButton size="small" color="primary"
                                onClick={() => setDlgEmail({ open: true, cliente: f.cliente, factura: f })}>
                                <Email fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Estado de Cuenta">
                              <IconButton size="small"
                                onClick={() => navigate(`/cuentas-cobrar/estado-cuenta/${clienteRuc || f.cliente?.id}`)}>
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {facsFiltradas.length > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5, bgcolor: '#e3f2fd', borderTop: '2px solid #1565c0' }}>
                <Typography variant="body2" fontWeight={700}>{facsFiltradas.length} documento(s) pendiente(s)</Typography>
                <Typography variant="body2" fontWeight={700} color="error.main">Total: {fmt(totalFiltrado)}</Typography>
              </Box>
            )}
          </Paper>
        </>
      )}

      {/* ══════════════ TAB 1: ANÁLISIS ══════════════ */}
      {tab === 1 && (
        <>
          {loadingAnalisis ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
          ) : analisis ? (
            <>
              {/* Semáforo + métricas clave */}
              <div style={{ display: 'grid', gridTemplateColumns: 'auto repeat(4,1fr)', gap: 12, marginBottom: 20, alignItems: 'center' }}>
                <SemaforoGlobal estado={analisis.semaforo} />
                <Kpi icon={<TrendingUp />}  label="Tasa morosidad"    value={pct(analisis.tasaMorosidad)}    color="#b71c1c" bg="#fce4ec" />
                <Kpi icon={<CheckCircle />} label="Tasa cobranza"     value={pct(analisis.tasaCobranza)}     color="#2e7d32" bg="#e8f5e9" />
                <Kpi icon={<AccessTime />}  label="Días prom. cobro"  value={`${analisis.diasPromedioCobro}d`} color="#1565c0" bg="#e3f2fd" />
                <Kpi icon={<Warning />}     label="Saldo vencido +90" value={fmt(analisis.saldoVencido)}     color="#e65100" bg="#fff3e0" />
              </div>

              {/* Gráficas: tendencia + antigüedad */}
              <div style={{ display: 'grid', gridTemplateColumns: '60% 38%', gap: 16, marginBottom: 20 }}>
                <Paper sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="body2" fontWeight={700} gutterBottom>📈 Tendencia de Cobros ({new Date().getFullYear()})</Typography>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={analisis.tendencia} margin={{ left: 8, right: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="mes" fontSize={11} />
                      <YAxis fontSize={11} tickFormatter={v => `$${v}`} width={64} />
                      <RTooltip formatter={(v, n) => [`$${v.toFixed(2)}`, n]} />
                      <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="emitido"   name="Emitido"   fill="#90caf9" radius={[2,2,0,0]} />
                      <Bar dataKey="cobrado"   name="Cobrado"   fill="#4caf50" radius={[2,2,0,0]} />
                      <Bar dataKey="pendiente" name="Pendiente" fill="#f44336" radius={[2,2,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
                <Paper sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="body2" fontWeight={700} gutterBottom>⏳ Distribución por Antigüedad</Typography>
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={analisis.distAntiguedad.filter(d => d.valor > 0)} dataKey="valor" nameKey="rango" cx="50%" cy="50%" outerRadius={70} label={({ rango, percent }) => `${rango} ${(percent*100).toFixed(0)}%`} labelLine={false} fontSize={10}>
                        {analisis.distAntiguedad.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <RTooltip formatter={(v) => fmt(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                  <Box sx={{ mt: 1 }}>
                    {analisis.distAntiguedad.map((d, i) => (
                      <Box key={d.rango} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: PIE_COLORS[i] }} />
                          <Typography variant="caption">{d.rango} días</Typography>
                        </Box>
                        <Typography variant="caption" fontWeight={700}>{fmt(d.valor)}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              </div>

              {/* Top clientes morosos */}
              <Paper sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="body2" fontWeight={700} gutterBottom>🔴 Top Clientes con Mayor Deuda</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f9fafb' }}>
                      {['#','Cliente','RUC','Facturas','Saldo Pendiente','Días máx.','Riesgo'].map(h => (
                        <TableCell key={h} sx={{ fontSize: 11, fontWeight: 700, py: 0.75 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analisis.topClientes.map((c, i) => {
                      const ec = EDAD_COLOR(c.diasMaximo);
                      return (
                        <TableRow key={c.ruc} hover>
                          <TableCell>
                            <Avatar sx={{ width: 26, height: 26, fontSize: 12, bgcolor: i === 0 ? '#d32f2f' : i < 3 ? '#f57f17' : '#1565c0' }}>
                              {i+1}
                            </Avatar>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>{c.nombre}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{c.ruc}</TableCell>
                          <TableCell align="center">{c.facturas}</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: '#d32f2f' }}>{fmt(c.saldo)}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'inline-flex', px: 1, py: 0.25, borderRadius: 1, bgcolor: ec.bg, border: `1px solid ${ec.border}` }}>
                              <Typography sx={{ fontSize: 11, fontWeight: 700, color: ec.text }}>{c.diasMaximo}d</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {c.diasMaximo > 90 ? <Chip label="Crítico"  color="error"   size="small" sx={{ fontSize: 10, height: 20 }} />
                            : c.diasMaximo > 60 ? <Chip label="Alto"     color="warning" size="small" sx={{ fontSize: 10, height: 20 }} />
                            : c.diasMaximo > 30 ? <Chip label="Medio"    color="info"    size="small" sx={{ fontSize: 10, height: 20 }} />
                                                : <Chip label="Bajo"     color="success" size="small" sx={{ fontSize: 10, height: 20 }} />}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Paper>
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography color="text.secondary">No hay datos de análisis disponibles</Typography>
            </Box>
          )}
        </>
      )}

      {/* ══════════════ TAB 2: POR CLIENTE ══════════════ */}
      {tab === 2 && (
        <Paper sx={{ borderRadius: 2 }}>
          <Box sx={{ px: 2, py: 1.5, bgcolor: '#1565c0', color: '#fff', borderRadius: '8px 8px 0 0' }}>
            <Typography fontWeight={700}>Clientes con Saldo Pendiente</Typography>
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f7fa', '& th': { fontWeight: 700, fontSize: 11, py: 1 } }}>
                <TableCell>Cliente</TableCell>
                <TableCell>Identificación</TableCell>
                <TableCell align="center">Facturas Pend.</TableCell>
                <TableCell align="right">Saldo Total</TableCell>
                <TableCell sx={{ minWidth: 120 }}>Indicador</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clientes.length === 0 ? (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary">No hay clientes con saldo pendiente</Typography>
                </TableCell></TableRow>
              ) : (
                clientes.map(c => {
                  const maxSaldo = Math.max(...clientes.map(x => parseFloat(x.saldo_total || 0)));
                  const pct2 = maxSaldo > 0 ? (parseFloat(c.saldo_total || 0) / maxSaldo) * 100 : 0;
                  return (
                    <TableRow key={c.cliente_id} hover sx={{ '& td': { fontSize: 12, py: 0.9 } }}>
                      <TableCell sx={{ fontWeight: 600 }}>{c.cliente_nombre}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{c.cliente_identificacion}</TableCell>
                      <TableCell align="center">
                        <Chip label={c.facturas_pendientes} size="small" color="warning" sx={{ fontWeight: 700 }} />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#d32f2f' }}>{fmt(c.saldo_total)}</TableCell>
                      <TableCell sx={{ minWidth: 120 }}>
                        <LinearProgress variant="determinate" value={pct2}
                          sx={{ height: 8, borderRadius: 4, bgcolor: '#e0e0e0', '& .MuiLinearProgress-bar': { bgcolor: pct2 > 75 ? '#f44336' : pct2 > 40 ? '#ff9800' : '#4caf50' } }} />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Estado de Cuenta">
                          <IconButton size="small" color="primary"
                            onClick={() => navigate(`/cuentas-cobrar/estado-cuenta/${c.cliente_id}`)}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Registrar Cobro">
                          <IconButton size="small" color="success"
                            onClick={() => navigate(`/cuentas-cobrar/registrar-cobro?clienteId=${c.cliente_id}`)}>
                            <Payment fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Enviar Recordatorio">
                          <IconButton size="small" color="info"
                            onClick={() => setDlgEmail({ open: true, cliente: { nombre: c.cliente_nombre, ruc: c.cliente_identificacion }, factura: { saldo: c.saldo_total } })}>
                            <Email fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* ── DIALOG RECORDATORIO ── */}
      <Dialog open={dlgEmail.open} onClose={() => setDlgEmail({ open: false, cliente: null, factura: null })} maxWidth="sm" fullWidth>
        <DialogTitle>📧 Enviar Recordatorio de Cobro</DialogTitle>
        <DialogContent>
          {dlgEmail.cliente && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f5f7fa', borderRadius: 1 }}>
              <Typography variant="body2"><strong>Cliente:</strong> {dlgEmail.cliente.nombre}</Typography>
              <Typography variant="body2"><strong>Saldo pendiente:</strong> {fmt(dlgEmail.factura?.saldo)}</Typography>
            </Box>
          )}
          <TextField fullWidth multiline rows={4} label="Mensaje personalizado (opcional)"
            value={mensajeEmail} onChange={e => setMensajeEmail(e.target.value)}
            placeholder={`Estimado/a ${dlgEmail.cliente?.nombre || 'cliente'}, le recordamos que tiene un saldo pendiente de ${fmt(dlgEmail.factura?.saldo)}...`} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            * Se enviará al correo registrado del cliente usando el SMTP configurado en tu empresa.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgEmail({ open: false, cliente: null, factura: null })}>Cancelar</Button>
          <Button variant="contained" onClick={enviarRecordatorio} disabled={enviandoEmail} startIcon={<Email />}>
            {enviandoEmail ? 'Enviando...' : 'Enviar Recordatorio'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
